import { PopupModal } from "../../../../components/Modal";
import { Chip } from "./Chip";
import { fmtActor, fmtDateTime } from "../format";
import type { LockEventRow } from "../types";

/** The full symmetric lock event log — who, when, why, auto tags, and on
 *  removals the skipped-run count + TDSD consequence (§5.2).
 *
 *  Rows are removable from this reading of the log, which is NOT the same as
 *  lifting a lock: the derivation still reads every event, so a lock whose
 *  apply event is hidden here is still applied. The lock's own apply/remove
 *  buttons are on the page behind this modal. */
export function LockHistoryModal({
  history,
  onClose,
  onRemoveRow,
}: {
  history: LockEventRow[];
  onClose: () => void;
  onRemoveRow?: (event: LockEventRow) => void;
}) {
  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto w-full max-w-2xl max-h-[85dvh] overflow-y-auto"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-content-primary">
          Lock history
        </h3>
        <button
          onClick={onClose}
          className="text-content-tertiary hover:text-content-primary transition-colors text-xl leading-none"
        >
          ×
        </button>
      </div>
      {history.length === 0 ? (
        <p className="text-sm text-gray-500">No lock events on record.</p>
      ) : (
        <div className="flex flex-col">
          {history.map((event) => (
            <div
              key={event.id}
              className="group py-2.5 border-b border-[#E9E9EB] last:border-b-0"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <Chip tone={event.lock === "validator" ? "purple" : "neutral"}>
                  {event.lock} lock
                </Chip>
                <span className="text-sm font-medium text-content-primary">
                  {event.action === "apply" ? "applied" : "removed"}
                </span>
                {event.auto && <Chip tone="neutral">auto-applied</Chip>}
                <span className="text-xs text-gray-500">
                  {fmtDateTime(event.at)} · {fmtActor(event.actor)}
                </span>
                {onRemoveRow && (
                  <button
                    onClick={() => onRemoveRow(event)}
                    data-tip="Hide this row from the log — the lock itself is unaffected"
                    className="ml-auto px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-400 hover:text-danger-dark hover:bg-danger-light/50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">{event.reason}</div>
              {event.action === "remove" && event.tdsdReset !== null && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {event.tdsdReset
                    ? `trusted data start date reset (${event.skippedRuns ?? 0} skipped runs): ${event.previousTdsd ?? "—"} → ${event.newTdsd ?? "—"}`
                    : `trusted data start date unchanged (${event.skippedRuns ?? 0} skipped runs)`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PopupModal>
  );
}
